import React, { useState } from "react";
import { motion } from "framer-motion";
import { User, Briefcase, Send } from "lucide-react";
import { FormInput } from "./FormInput";
import { ServiceTag } from "./ServiceTag";
import { toast } from "react-toastify"; // अगर आप toast use कर रहे हो

const seekerOptions = [
  "Drone Vendorship",
  "Drones",
  "Drone Parts",
  "Drone Jobs",
  "Drone Courses",
  "RPTO",
  "Drone As a Service",
  "Drone Pilot",
  "Drone Content Writer",
  "Drone Solution Provider",
  "Drone Marketing",
  "Drone Social Media",
  "Drone Influencer",
  "Other",
];

const providerOptions = [...seekerOptions];

export const ServiceForm = ({ onClose }) => {
  const [role, setRole] = useState("seekers"); // seekers OR providers
  const isProviderForm = role === "providers";
const containerStyle = {
    display: "flex",
    alignItems: "center",
    padding: "4px",
    border: "1px solid #475569", // slate-600
    borderRadius: "9999px", // pill shape
    background: "transparent",
    width: "fit-content",
    margin: "auto",
  };

  const baseButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 24px",
    border: "none",
    borderRadius: "9999px",
    fontSize: "14px",
    fontWeight: 600,
    background: "transparent",
    color: "#94a3b8", // slate-300
    cursor: "pointer",
    transition: "all 0.3s ease",
  };

  const activeStyle = {
    background: "#2563eb", // blue-600
    color: "white",
  };

  const [seekerFormData, setSeekerFormData] = useState({
    name: "",
    phone: "",
    city: "",
    state: "",
    email: "",
    interest: [],
  });

  const [providerFormData, setProviderFormData] = useState({
    name: "",
    phone: "",
    city: "",
    state: "",
    email: "",
    interest: [],
  });

  const [otherSeeker, setOtherSeeker] = useState("");
  const [otherProvider, setOtherProvider] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (isProviderForm) {
      setProviderFormData({ ...providerFormData, [name]: value });
    } else {
      setSeekerFormData({ ...seekerFormData, [name]: value });
    }
  };

  const handleServiceSelect = (service) => {
    if (isProviderForm) {
      setProviderFormData((prev) => {
        const interest = prev.interest.includes(service)
          ? prev.interest.filter((s) => s !== service)
          : [...prev.interest, service];
        return { ...prev, interest };
      });
    } else {
      setSeekerFormData((prev) => {
        const interest = prev.interest.includes(service)
          ? prev.interest.filter((s) => s !== service)
          : [...prev.interest, service];
        return { ...prev, interest };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentFormData = isProviderForm ? providerFormData : seekerFormData;
    const currentOther = isProviderForm ? otherProvider : otherSeeker;

    if (!currentFormData.name || !currentFormData.phone || !currentFormData.email) {
      setMessage("Please fill in all required fields");
      return;
    }

    setMessage("");
    setIsLoading(true);

    let updatedInterests = [...currentFormData.interest];
    const otherText = currentOther.trim();

    if (updatedInterests.includes("Other") && otherText) {
      const index = updatedInterests.indexOf("Other");
      updatedInterests[index] = otherText;
    }

    const payload = {
      ...currentFormData,
      interest: updatedInterests,
      other: otherText,
    };

    try {
      const apiEndpoint = isProviderForm ? "/serviceProvider" : "/droneXploreData";

      const response = await fetch(process.env.REACT_APP_API_URL + apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Form submitted successfully!");
        if (isProviderForm) {
          setProviderFormData({
            name: "",
            phone: "",
            city: "",
            state: "",
            email: "",
            interest: [],
          });
          setOtherProvider("");
        } else {
          setSeekerFormData({
            name: "",
            phone: "",
            city: "",
            state: "",
            email: "",
            interest: [],
          });
          setOtherSeeker("");
        }
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || "Submission failed. Please try again.");
      }
    } catch (error) {
      setMessage("Network error. Please try again later.");
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const options = isProviderForm ? providerOptions : seekerOptions;
  const formData = isProviderForm ? providerFormData : seekerFormData;

  return (
    <div className="relative max-w-2xl mx-auto mt-12 backdrop-blur-lg p-8 rounded-2xl border border-slate-700 shadow-2xl shadow-black/40">
      <motion.form
        key={role}
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
        onSubmit={handleSubmit}
        className="flex flex-col gap-7"
      >
        {/* Toggle */}
        <motion.div
          className="relative p-1.5 rounded-full flex border border-slate-600 w-fit mx-auto bg-transparent"
        >
           <button
        type="button"
        onClick={() => setRole("seekers")}
        style={{
          ...baseButtonStyle,
          ...(role === "seekers" ? activeStyle : {}),
        }}
      >
        <User size={16} /> Seeker
      </button>

      <button
        type="button"
        onClick={() => setRole("providers")}
        style={{
          ...baseButtonStyle,
          ...(role === "providers" ? activeStyle : {}),
        }}
      >
        <Briefcase size={16} /> Provider
      </button>
        </motion.div>

        {/* Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            label="Name*"
            name="name"
            value={formData.name}
            onChange={handleFieldChange}
            placeholder="Enter your full name"
          />
          <FormInput
            label="Phone*"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              // Allow only digits
              const value = e.target.value.replace(/\D/g, "");

              // Limit to 10 digits
              if (value.length <= 10) {
                handleFieldChange({
                  target: {
                    name: "phone",
                    value,
                  },
                });
              }
            }}
            placeholder="Enter your phone number"
            maxLength={10} // extra safety
          />

          <FormInput
            label="City"
            name="city"
            value={formData.city}
            onChange={handleFieldChange}
            placeholder="Enter your city"
          />
          <FormInput
            label="State"
            name="state"
            value={formData.state}
            onChange={handleFieldChange}
            placeholder="Enter your state"
          />
        </div>
        <FormInput
          label="Email Address*"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleFieldChange}
          placeholder="you@example.com"
        />

        {/* Services */}
        <div>
          <label className="text-sm font-bold text-gray-200 mb-3 block">
            {isProviderForm ? "What services do you provide?" : "Which services do you need?"}
          </label>
          <div className="flex flex-wrap gap-3 items-center">
            {options.map((option) => (
              <div key={option} className="flex items-center gap-2">
                <ServiceTag
                  label={option}
                  selected={formData.interest.includes(option)}
                  onSelect={handleServiceSelect}
                />
                {option === "Other" && formData.interest.includes("Other") && (
                  <input
                    type="text"
                    placeholder="Please specify"
                    value={isProviderForm ? otherProvider : otherSeeker}
                    onChange={(e) =>
                      isProviderForm ? setOtherProvider(e.target.value) : setOtherSeeker(e.target.value)
                    }
                    className="px-3 py-2 bg-transparent text-white border border-slate-600 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error / Message */}
        {message && <p className="text-red-400 text-sm">{message}</p>}

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-3.5 px-6 rounded-lg text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50"
        >
          {isLoading ? "Submitting..." : <><Send className="w-5 h-5" /> Submit</>}
        </motion.button>
      </motion.form>
    </div>
  );
};
